import taskManagerApi from '@/features/shared/services/taskManagerApi';
import { updateUserSchema } from '@/features/user/validators/userValidator';
/**
 * Fetch users with optional pagination, filters, and an "all" flag.
 * - If page is provided, we do paginated fetch.
 * - If all is true, we fetch all users.
 * - If filters are provided, they are merged into params.
 */
export async function fetchUsers(page, filters, all = false, limit) {
    try {
        const params = {
            ...(page ? { page } : {}),
            ...(filters || {}),
            ...(all ? { all: true } : {}),
            ...(limit ? { limit } : {}),
        };
        const response = await taskManagerApi.get('/user', { params });
        // Typically: { status: 'SUCCESS', data: { users: [...], meta: {...} } }
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la récupération des utilisateurs");
        }
    }
}
/**
 * Create a new user.
 */
export async function createUser(newUser) {
    try {
        const response = await taskManagerApi.post('/user', newUser);
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la création de l'utilisateur");
        }
    }
}
/**
 * Send account activation email to a newly created user.
 */
export async function sendActivationEmail(userId) {
    try {
        await taskManagerApi.post(`/user/send-activation/${userId}`);
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de l'envoi de l'email d'activation");
        }
    }
}
/**
 * Send reset password email for a user by ID.
 */
export async function sendResetPasswordEmail(userId) {
    try {
        await taskManagerApi.post(`/user/reset-password/${userId}`);
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la réinitialisation du mot de passe");
        }
    }
}
/**
 * Update an existing user by ID.
 * Also performs client-side validation using updateUserSchema.
 */
export async function updateUser(userId, userData) {
    // Validate data before sending
    const { error, value } = updateUserSchema.validate(userData, { abortEarly: false });
    if (error) {
        const errorMessages = error.details.map((err) => err.message).join(', ');
        throw new Error(`Validation échouée : ${errorMessages}`);
    }
    try {
        const response = await taskManagerApi.put(`/user/${userId}`, value);
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la modification de l'utilisateur");
        }
    }
}
export async function updateUserManager(userId, managerId) {
    try {
        const response = await taskManagerApi.put(`/user/${userId}/manager`, { manager_id: managerId });
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la mise à jour du manager de l'utilisateur");
        }
    }
}
/**
 * Delete a user by ID.
 */
export async function deleteUser(userId) {
    try {
        const response = await taskManagerApi.delete(`/user/${userId}`);
        return response.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la suppression de l'utilisateur");
        }
    }
}
